import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchPartner, applyPartner } from '@/lib/api'
import { Card } from '@/components/Surface'
import { SubPage } from '@/components/SubPage'
import { haptic } from '@/lib/tg'

function Tile({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <Card className="p-3 text-center">
      <div className="num text-[20px] font-semibold" style={{ color: accent ? 'var(--color-accent)' : 'var(--color-ink)' }}>{value}</div>
      <div className="text-[11px] mt-0.5" style={{ color: 'var(--color-muted)' }}>{label}</div>
    </Card>
  )
}

export function Partner() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['partner'], queryFn: fetchPartner })
  const [note, setNote] = useState('')
  const [copied, setCopied] = useState('')
  const apply = useMutation({
    mutationFn: () => applyPartner(note.trim()),
    onSuccess: () => { haptic('medium'); qc.invalidateQueries({ queryKey: ['partner'] }) },
  })

  const copy = async (text: string, tag: string) => {
    haptic('light')
    try { await navigator.clipboard.writeText(text) } catch { /* ignore */ }
    setCopied(tag); setTimeout(() => setCopied(''), 1500)
  }

  const status = data?.status

  // ── active partner cabinet ──────────────────────────────────────────────
  if (status === 'active') {
    return (
      <SubPage title="партнёрство" subtitle="твой кабинет">
        <div className="px-5 space-y-3 stagger">
          <div className="grid grid-cols-2 gap-2">
            <Tile label="привёл" value={data?.invited ?? 0} accent />
            <Tile label="оплатили" value={data?.conversions ?? 0} />
            <Tile label="заработано" value={`${Math.round(data?.total_earned_rub ?? 0)} ₽`} />
            <Tile label="к выплате" value={`${Math.round(data?.accrued_rub ?? 0)} ₽`} accent />
          </div>

          <Card className="p-5">
            <div className="text-[13px]" style={{ color: 'var(--color-muted)' }}>твой промокод</div>
            <div className="num text-[22px] font-bold mt-1 tracking-wide">{data?.code}</div>
            {!!data?.buyer_discount && (
              <div className="text-[12px] mt-1" style={{ color: 'var(--color-muted)' }}>
                даёт покупателю скидку {data.buyer_discount}%
              </div>
            )}
            <button onClick={() => copy(data?.code || '', 'code')}
              className="tap w-full mt-3 py-2.5 rounded-[6px] text-[13.5px] font-semibold border"
              style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)', color: 'var(--color-ink)' }}>
              {copied === 'code' ? '✓ скопировано' : 'скопировать код'}
            </button>
          </Card>

          <Card className="p-5">
            <div className="text-[13px]" style={{ color: 'var(--color-muted)' }}>твоя ссылка</div>
            <div className="mt-2 num text-[13px] break-all leading-snug">{data?.ref_link?.replace('https://', '')}</div>
            <button onClick={() => copy(data?.ref_link || '', 'link')}
              className="tap w-full mt-3 py-2.5 rounded-[6px] text-[13.5px] font-semibold"
              style={{ background: 'var(--color-crimson)', color: '#fff' }}>
              {copied === 'link' ? '✓ скопировано' : 'скопировать ссылку'}
            </button>
          </Card>

          {!!(data?.payouts && data.payouts.length) && (
            <div>
              <h2 className="display text-[18px] mt-2 mb-2 px-1">выплаты</h2>
              <Card className="overflow-hidden">
                {data.payouts.map((p, i, arr) => (
                  <div key={i} className="px-5 py-3.5 flex items-center justify-between"
                    style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--color-line)' : 'none' }}>
                    <div className="num text-[12px]" style={{ color: 'var(--color-muted)' }}>
                      {new Date(p.created_at).toLocaleDateString('ru')}
                    </div>
                    <div className="num text-[15px] font-semibold">{Math.round(p.amount_rub)} ₽</div>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </div>
      </SubPage>
    )
  }

  // ── pending ─────────────────────────────────────────────────────────────
  if (status === 'pending') {
    return (
      <SubPage title="партнёрство" subtitle="заявка отправлена">
        <div className="px-5">
          <Card className="p-6 text-center">
            <div className="text-[15px] font-semibold">⏳ Заявка на рассмотрении</div>
            <div className="text-[13.5px] mt-2 leading-relaxed" style={{ color: 'var(--color-muted)' }}>
              Мы её изучим и свяжемся с тобой.
            </div>
          </Card>
        </div>
      </SubPage>
    )
  }

  // ── none / rejected / disabled → neutral application screen ─────────────
  return (
    <SubPage title="партнёрство" subtitle="сотрудничество">
      <div className="px-5 space-y-3">
        <Card className="p-5">
          <div className="text-[15px] font-semibold">🤝 Партнёрская программа</div>
          <div className="text-[13.5px] mt-2 leading-relaxed" style={{ color: 'var(--color-muted)' }}>
            {status === 'rejected'
              ? 'Заявка была отклонена. Можешь оставить новую.'
              : 'Для блогеров и владельцев каналов. Оставь заявку — мы свяжемся.'}
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Коротко о себе: ссылка на канал/блог и примерная аудитория (необязательно)"
            rows={4}
            className="w-full mt-3 rounded-[8px] px-3 py-2.5 text-[13.5px] outline-none resize-none"
            style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-line)', color: 'var(--color-ink)' }}
          />
          <button
            onClick={() => apply.mutate()}
            disabled={apply.isPending}
            className="tap w-full mt-3 py-3 rounded-[6px] text-[14px] font-semibold"
            style={{ background: 'var(--color-crimson)', color: '#fff', opacity: apply.isPending ? 0.6 : 1 }}>
            {apply.isPending ? 'отправляем…' : 'оставить заявку'}
          </button>
        </Card>
      </div>
    </SubPage>
  )
}
